import React from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { translate, TranslateOptionsParams } from '@fiverr-private/i18n-react';
import { diffTimeZonesInHours, getGMTOffset } from '../../../utils/time';
import { Preference } from '../Preference';
import { Gql } from '../../../types';

type Timezone = Gql.DmBriefTimezone;

const T_PREFIX = 'dynamic_matching.freelancer_preferences.fields.timezone';
const getTranslation = (key: string, params?: TranslateOptionsParams) => translate(`${T_PREFIX}.${key}`, params);

const getTimezoneText = ({ timezoneId, maxOffsetHours }: Timezone) =>
  getTranslation('text', {
    params: {
      timezone: getGMTOffset(timezoneId),
      hours: maxOffsetHours,
    },
  });

export const TimezonePreference = ({
  timezone,
  matchIndicator,
  sellerName,
  sellerTimezone,
}: {
  timezone: Timezone;
  matchIndicator?: Gql.Maybe<Gql.DmBriefMatchIndicator>;
  sellerName?: string;
  sellerTimezone?: string | null;
}) => (
  <Preference
    name="timezone"
    matchIndicator={matchIndicator}
    tooltipContent={
      sellerTimezone && (
        <TooltipText sellerName={sellerName} sellerTimezone={sellerTimezone} buyerTimezone={timezone.timezoneId} />
      )
    }
  >
    <Stack width="100%" wrap="wrap" flexShrink={1}>
      <Typography color="grey_1200" as="p" size="b_md">
        {getTimezoneText(timezone)}
      </Typography>
    </Stack>
  </Preference>
);

const TooltipText = ({
  sellerName,
  sellerTimezone,
  buyerTimezone,
}: {
  sellerName?: string;
  sellerTimezone: string;
  buyerTimezone: string;
}) => {
  const diff = diffTimeZonesInHours(sellerTimezone, buyerTimezone);
  const sellerGmtTimezone = getGMTOffset(sellerTimezone);
  const userType = sellerName ? 'buyer' : 'seller';

  const messagePrefix = getTranslation(`tooltip.${userType}_timezone`, {
    params: { timezone: sellerGmtTimezone },
  });
  const message = `${getTranslation('tooltip.hours', { params: { hours: Math.abs(diff) } })}
      ${diff > 0 ? getTranslation('tooltip.ahead') : getTranslation('tooltip.behind')}`;
  const messageSuffix = getTranslation(`tooltip.${userType}_message`);

  const buyerText = `${sellerName} ${messagePrefix} ${message} ${messageSuffix}`;

  const sellerText = `${messagePrefix} ${message} ${messageSuffix}`;

  return (
    <Stack direction="row" gap="0">
      <Typography>{sellerName ? buyerText : sellerText}</Typography>
    </Stack>
  );
};
